import { Box, Divider, Typography } from '@mui/material';
import { Controller } from 'react-hook-form';
import { EditClientStepProps } from '../../../types';
import { JSX } from 'react';
import { MARRIED_MARITAL_STATUS_ID } from '../../../constants';
import { NaturalPerson } from '../../../../common/types/client';
import { validationsErrorMessages } from '../../../../common/lib/validations';
import AsyncFormSelect from '../../../../common/components/form/asyncFormSelect';
import BooleanSelect from '../../../../common/components/form/booleanSelect';
import NumberTextField from '../../../../common/components/form/numberTextField';

/**
 * Natural person rating details edit form
 */
const RatingDetailsStep = ({ control, watch }: EditClientStepProps<NaturalPerson>): JSX.Element => {
  const watchMaritalStatusID = watch('maritalStatusID');

  return (
    <Box>
      <Box className="grid gap-8 md:grid-cols-2 mb-8">
        <Controller
          name="tobacco"
          control={control}
          render={({ field, fieldState: { error } }): JSX.Element => (
            <BooleanSelect
              {...field}
              label="Has the person used tobacco in the last 6 months? *"
              error={!!error}
            />
          )}
        />
        <Controller
          name="weight"
          control={control}
          render={({ field, fieldState: { error } }): JSX.Element => (
            <NumberTextField
              {...field}
              suffix=" kg"
              placeholder="eg. 65"
              label="Weight"
              helperText={error?.message}
              error={!!error}
            />
          )}
        />
        <Controller
          name="height"
          control={control}
          render={({ field, fieldState: { error } }): JSX.Element => (
            <NumberTextField
              {...field}
              label="Height"
              suffix=" cm"
              placeholder="eg. 120"
              helperText={error?.message}
              error={!!error}
            />
          )}
        />
      </Box>

      <Typography variant="h6">Education and employment</Typography>
      <Divider />

      <Box className="my-8 grid gap-8 md:grid-cols-2">
        <Controller
          name="educationID"
          control={control}
          rules={{
            required: { value: true, message: validationsErrorMessages.required },
            min: { value: 1, message: validationsErrorMessages.required },
          }}
          render={({ field, fieldState: { error } }): JSX.Element => (
            <AsyncFormSelect
              {...field}
              label="Person's highest education level *"
              error={Boolean(error)}
              helperText={error?.message}
              optionsEndpoint="/Educations"
            />
          )}
        />
        <Controller
          name="occupationID"
          control={control}
          rules={{
            required: { value: true, message: validationsErrorMessages.required },
            min: { value: 1, message: validationsErrorMessages.required },
          }}
          render={({ field, fieldState: { error } }): JSX.Element => (
            <AsyncFormSelect
              {...field}
              label="Occupation *"
              error={Boolean(error)}
              helperText={error?.message}
              optionsEndpoint="/Occupations"
            />
          )}
        />
        <Controller
          name="income"
          control={control}
          rules={{
            required: { value: true, message: validationsErrorMessages.required },
            min: { value: 1, message: validationsErrorMessages.required },
          }}
          render={({ field, fieldState: { error } }): JSX.Element => (
            <NumberTextField
              {...field}
              prefix="R "
              label="Gross monthly income *"
              placeholder="e.g. R24,500"
              helperText={error?.message}
              error={!!error}
            />
          )}
        />
        <Controller
          name="selfemployed"
          control={control}
          render={({ field, fieldState: { error } }): JSX.Element => (
            <BooleanSelect {...field} label="Self-employed? *" error={!!error} />
          )}
        />
      </Box>
      {watchMaritalStatusID === MARRIED_MARITAL_STATUS_ID && (
        <>
          <Typography variant="h6">Spouse education and employment</Typography>
          <Divider />
          <Box className="mt-8 grid gap-8 md:grid-cols-2">
            <Controller
              name="spouseEducationID"
              control={control}
              rules={{
                required: { value: true, message: validationsErrorMessages.required },
                min: { value: 1, message: validationsErrorMessages.required },
              }}
              render={({ field, fieldState: { error } }): JSX.Element => (
                <AsyncFormSelect
                  {...field}
                  label="Person's highest education level *"
                  error={Boolean(error)}
                  optionsEndpoint="/Educations"
                  helperText={error?.message}
                />
              )}
            />
            <Controller
              name="spouseIncome"
              control={control}
              rules={{
                required: { value: true, message: validationsErrorMessages.required },
                min: { value: 1, message: validationsErrorMessages.required },
              }}
              render={({ field, fieldState: { error } }): JSX.Element => (
                <NumberTextField
                  {...field}
                  prefix="R "
                  label="Gross monthly income *"
                  placeholder="e.g. R24,500"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
            <Controller
              name="spouseIsSelfEmployed"
              control={control}
              render={({ field, fieldState: { error } }): JSX.Element => (
                <BooleanSelect {...field} label="Self-employed? *" error={!!error} />
              )}
            />
            <Controller
              name="spouseOccupationID"
              control={control}
              render={({ field, fieldState: { error } }): JSX.Element => (
                <AsyncFormSelect
                  {...field}
                  label="Occupation"
                  error={Boolean(error)}
                  optionsEndpoint="/Occupations"
                  helperText={error?.message}
                />
              )}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default RatingDetailsStep;
